import {
  type ChatClientOptions,
  clearStoredSession,
  createBrowserStorage,
  type IdentityScope,
  identityScope,
} from '@plivo/chat-client'
import { getLocalAuth } from 'v2/utils'
import dayjs from 'v2/utils/dayjs'
import { getBuddyFlowConfig } from './flow-config'

// The flow conversation's contact id, one per console member (aom) per tab, like old Buddy's
// thread. The chat SDK keys its stored session by this id, so a different member or org in
// the same browser never sees another's thread.
// Storage access is best-effort: without it the conversation just won't survive a reload.
const CONTACT_KEY_PREFIX = 'buddy_flow_contact:'

// The runner rejects a contact_identifier that is not a UUID.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// A turn can run for minutes, so a conversation retires this long before its token expires.
const EXPIRY_MARGIN_MS = 5 * 60_000

// Tests replace this on the object and restore it, instead of mocking the module.
export const conversationClock = { now: (): number => Date.now() }

const newUuid = (): string => crypto.randomUUID()

const storageKey = (owner: string): string => `${CONTACT_KEY_PREFIX}${owner}`

// The conversation's identity token works only until it expires and only for the console login
// that minted it. Kept under the contact prefix, so logout's sweep removes it too.
const tokenKey = (owner: string): string => `${storageKey(owner)}:token`

interface TokenInfo {
  // Epoch ms, or null when this browser's clock cannot judge the expiry.
  expiresAt: number | null
  login: string
}

// One conversation per tab, as old Buddy had: sessionStorage survives a reload but not a new
// tab, so two tabs don't drift apart on one shared conversation.
export const tabStorage = (): ChatClientOptions['storage'] => {
  try {
    return createBrowserStorage({ storage: window.sessionStorage })
  } catch {
    return undefined
  }
}

// The scope the SDK stores a contact's conversation under.
export const conversationScope = (appId: string, contactId: string): IdentityScope =>
  identityScope(appId.trim().toLowerCase(), contactId, 'anonymous')

// A logout and login in any tab changes the session token every tab reads. Only a hash is
// kept, so the token itself stays out of sessionStorage.
const currentLogin = (): string => {
  const token = getLocalAuth()?.auth_token ?? ''
  let hash = 0x811c9dc5
  for (let i = 0; i < token.length; i++) hash = Math.imul(hash ^ token.charCodeAt(i), 0x01000193)
  return (hash >>> 0).toString(36)
}

// An expiry already inside the margin means a skewed clock or a very short token; trusting it
// would retire every new conversation at once.
const expiryOf = (tokenExpiresAt?: string): number | null => {
  const expiresAt = tokenExpiresAt ? dayjs(tokenExpiresAt).valueOf() : Number.NaN
  return expiresAt - conversationClock.now() > EXPIRY_MARGIN_MS ? expiresAt : null
}

// A contact id stored without token info predates it, so it counts as retired.
const isRetired = (owner: string): boolean => {
  try {
    const stored = sessionStorage.getItem(tokenKey(owner))
    const { expiresAt, login }: TokenInfo = JSON.parse(stored ?? '')
    if (login !== currentLogin()) return true
    return expiresAt !== null && conversationClock.now() >= expiresAt - EXPIRY_MARGIN_MS
  } catch {
    return true
  }
}

// A conversation whose identity token has expired, or came from an earlier login, is forgotten
// instead: the flow's account tools would refuse its token.
export const readContactId = (owner: string): string | null => {
  if (!owner) return null
  try {
    const stored = sessionStorage.getItem(storageKey(owner))
    if (!stored || !UUID_RE.test(stored)) return null
    if (!isRetired(owner)) return stored
    sessionStorage.removeItem(storageKey(owner))
    return null
  } catch {
    return null
  }
}

// The same check for a conversation already open in this tab. Nothing stored gives no verdict.
export const hasConversationRetired = (owner: string): boolean => {
  if (!owner) return false
  try {
    return sessionStorage.getItem(storageKey(owner)) !== null && isRetired(owner)
  } catch {
    return false
  }
}

// Starts a new conversation identity for this member and remembers it.
export const mintContactId = (owner: string, tokenExpiresAt?: string): string => {
  const contactId = newUuid()
  if (owner) {
    try {
      const info: TokenInfo = { expiresAt: expiryOf(tokenExpiresAt), login: currentLogin() }
      // Written first: a contact id stored without it reads as retired.
      sessionStorage.setItem(tokenKey(owner), JSON.stringify(info))
      sessionStorage.setItem(storageKey(owner), contactId)
    } catch {
      // Storage full or disabled: the id still works until the page reloads.
    }
  }
  return contactId
}

export const forgetContactId = (owner: string): void => {
  if (!owner) return
  try {
    sessionStorage.removeItem(storageKey(owner))
  } catch {
    // Nothing stored to forget.
  }
}

const takeAllContactIds = (): string[] => {
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i))
    return keys.flatMap((key) => {
      if (!key?.startsWith(CONTACT_KEY_PREFIX)) return []
      const contactId = sessionStorage.getItem(key)
      sessionStorage.removeItem(key)
      return contactId && UUID_RE.test(contactId) ? [contactId] : []
    })
  } catch {
    return []
  }
}

// For logout: each stored conversation's identity token is bound to the console session that
// is ending, so none may resume after the next login. The stored auth is read before the first
// await, so start this before localStorage is cleared.
export const forgetBuddyFlowConversations = async (): Promise<void> => {
  const appId = getBuddyFlowConfig()?.appId
  const contactIds = takeAllContactIds()
  const storage = tabStorage()
  if (!appId || !storage) return
  await Promise.all(
    contactIds.map(async (contactId) => {
      try {
        await clearStoredSession(storage, conversationScope(appId, contactId))
      } catch {
        // Best-effort, and never a failed logout: without its contact id it never resumes.
      }
    }),
  )
}
